import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';

/**
 * Reusable table for inbound/receipt item entry.
 *
 * Supports a default warehouse location and per-item custom location overrides,
 * matching the pattern used by work-order completion and purchase receipt flows.
 */
function InboundItemsTable({
  items = [],
  defaultLocation = 'main',
  onDefaultLocationChange,
  onItemChange,
  disabled = false,
  showPlanned = true,
  showReceived = false,
  receivedLabel = '已入库',
  quantityLabel = '入库数量',
  title = '入库明细',
  showDefaultLocationInput = true,
}) {
  const handleQuantityChange = (id, value) => {
    onItemChange(id, 'quantity', parseFloat(value) || 0);
  };

  const handleLocationChange = (id, value) => {
    onItemChange(id, 'location', value);
  };

  const handleCustomLocationToggle = (id, useCustom) => {
    onItemChange(id, 'use_custom_location', useCustom);
    if (!useCustom) {
      onItemChange(id, 'location', defaultLocation);
    }
  };

  return (
    <div className="space-y-4">
      {showDefaultLocationInput && (
        <div className="flex items-center justify-between">
          <Label className="text-base font-medium">{title}</Label>
          <div className="flex items-center gap-2">
            <Label className="text-sm text-muted-foreground">默认入库位置:</Label>
            <Input
              value={defaultLocation}
              onChange={(e) => onDefaultLocationChange(e.target.value)}
              className="w-32 h-8"
              placeholder="默认位置"
              disabled={disabled}
            />
          </div>
        </div>
      )}

      {!showDefaultLocationInput && title && (
        <Label className="text-base font-medium">{title}</Label>
      )}

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>产品代码</TableHead>
              <TableHead>产品名称</TableHead>
              {showPlanned && (
                <TableHead className="text-right">计划数量</TableHead>
              )}
              {showReceived && (
                <TableHead className="text-right">{receivedLabel}</TableHead>
              )}
              <TableHead className="text-right">{quantityLabel}</TableHead>
              <TableHead className="w-[100px] text-center">自定义位置</TableHead>
              <TableHead>入库位置</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((item) => (
              <TableRow key={item.id}>
                <TableCell className="font-mono text-sm">
                  {item.product_code || '-'}
                </TableCell>
                <TableCell className="text-sm">
                  {item.product_name || '-'}
                </TableCell>
                {showPlanned && (
                  <TableCell className="text-right">{item.planned_quantity}</TableCell>
                )}
                {showReceived && (
                  <TableCell className="text-right text-muted-foreground">
                    {item.already_received || 0}
                  </TableCell>
                )}
                <TableCell>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={item.quantity}
                    onChange={(e) => handleQuantityChange(item.id, e.target.value)}
                    className="w-24 h-8 text-right"
                    disabled={disabled}
                  />
                </TableCell>
                <TableCell className="text-center">
                  <Checkbox
                    checked={item.use_custom_location}
                    onCheckedChange={(checked) =>
                      handleCustomLocationToggle(item.id, checked)
                    }
                    disabled={disabled}
                  />
                </TableCell>
                <TableCell>
                  <Input
                    value={item.location}
                    onChange={(e) => handleLocationChange(item.id, e.target.value)}
                    className="w-32 h-8"
                    disabled={disabled || !item.use_custom_location}
                    placeholder={defaultLocation}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

export default InboundItemsTable;
